/**
 * Card Style Tokens
 * Consistent card styling patterns across the application
 */

export const CARDS = {
  // ─────────────────────────────────────────────────────────────────
  // BASE VARIANTS - Use these for consistent card sizing
  // ─────────────────────────────────────────────────────────────────
  base: "",  // Uses default ui/card.tsx styles (rounded-lg border bg-card shadow-sm)
  listItem: "p-2",  // Tight padding for list rows
  compact: "p-3",
  standard: "p-4 sm:p-6",
  spacious: "p-6 sm:p-8",
  horizontalCompact: "p-3 sm:p-4",  // Horizontal cards with image + content
  horizontalList: "p-2 sm:p-3",     // Tight horizontal list items
  
  // ─────────────────────────────────────────────────────────────────
  // STAT/METRIC CARDS - For KPI displays and dashboard metrics
  // ─────────────────────────────────────────────────────────────────
  stat: "p-4",  // Compact padding for stat cards
  statCompact: "p-3 rounded-lg border bg-card",  // Inline stat boxes
  statCard: "p-4 rounded-lg border bg-card",  // Standard stat card container
  statCompactBackground: "p-2 border rounded bg-background",  // Mini stat cells
  statMuted: "p-3 bg-muted rounded-lg text-center",  // Muted background stat boxes
  statMutedBorder: "p-3 rounded-lg border bg-muted/50",  // Muted with border
  statMutedLight: "p-3 rounded-lg bg-muted/50",  // Light muted stat containers (compact)
  statMutedStandard: "p-4 rounded-lg bg-muted/50",  // Standard muted stat containers
  statMutedStandardCenter: "p-4 rounded-lg bg-muted/50 text-center",  // Centered variant
  statBackgroundCompact: "p-2 bg-background rounded",  // Mini stat cells with background
  iconWrapper: "p-2 bg-muted rounded-lg",  // Icon container in stat cards
  
  // Stat card size variants (used by StatCard component)
  statHeader: "pb-2",
  statHeaderCompact: "pb-1",
  statValue: "text-2xl font-bold",
  statValueCompact: "text-lg font-semibold",
  statLabel: "text-sm font-medium flex items-center gap-2",
  statLabelCompact: "text-xs font-medium flex items-center gap-1.5 whitespace-nowrap",
  statSubtext: "text-xs text-muted-foreground",
  statSubtextCompact: "text-[11px] text-muted-foreground",
  statIconSize: "h-4 w-4 flex-shrink-0",
  statIconSizeCompact: "h-3.5 w-3.5 flex-shrink-0",
  statHeaderPaddingCompact: "p-3 pb-1",
  statContentPaddingCompact: "p-3 pt-0",
  iconWrapperLarge: "flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center",  // 40x40 icon containers
  
  // Status-colored stat boxes (for filled/missing indicators)
  statSuccess: "p-4 rounded-lg bg-emerald-500/10 text-center",  // Success/filled stats
  statWarning: "p-4 rounded-lg bg-amber-500/10 text-center",    // Warning/missing stats
  statSuccessAlt: "p-4 rounded-lg bg-green-500/10 text-center", // Alternative success (green)
  statWarningAlt: "p-4 rounded-lg bg-orange-500/10 text-center", // Alternative warning (orange)
  
  // ─────────────────────────────────────────────────────────────────
  // INTERACTIVE STATES - For clickable/selectable cards
  // ─────────────────────────────────────────────────────────────────
  interactive: "hover:border-primary/50 cursor-pointer transition-colors",
  statCardInteractive: "p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors",
  selected: "border-primary ring-1 ring-primary",
  placeholder: "border-dashed",
  disabled: "opacity-50 cursor-not-allowed",
  
  // ─────────────────────────────────────────────────────────────────
  // STATUS BORDERS - For status indication on cards
  // ─────────────────────────────────────────────────────────────────
  successBorder: "border-green-500/30 bg-green-500/5",
  warningBorder: "border-yellow-500/30 bg-yellow-500/5",
  errorBorder: "border-red-500/30 bg-red-500/5",
  infoBorder: "border-blue-500/30 bg-blue-500/5",
  
  // ─────────────────────────────────────────────────────────────────
  // SPECIAL PURPOSE - For specific use cases
  // ─────────────────────────────────────────────────────────────────
  highlight: "border-primary/20 bg-primary/5",
  muted: "bg-muted/30 border-border/50",
  elevated: "shadow-lg",
  flat: "shadow-none",
  
  // ─────────────────────────────────────────────────────────────────
  // BORDERED CONTAINERS - For inline bordered sections (not Card components)
  // ─────────────────────────────────────────────────────────────────
  bordered: "p-4 border rounded-lg",
  borderedMuted: "p-4 border rounded-lg bg-muted/30",
  borderedMutedLight: "p-4 border rounded-lg bg-muted/50",  // Light muted bordered container
  borderedCompact: "p-3 border rounded-lg",
  borderedCompactMuted: "p-3 border rounded-lg bg-muted/30",  // Compact bordered muted
  borderedInteractive: "p-4 border rounded-lg hover:bg-muted/50 transition-colors",
  
  // ─────────────────────────────────────────────────────────────────
  // INFO/MUTED CONTAINERS - For information boxes and descriptions
  // ─────────────────────────────────────────────────────────────────
  infoBox: "p-3 bg-muted rounded-lg",  // Role descriptions, selection info
  infoBoxMt: "mt-4 p-3 bg-muted rounded-lg",  // Info box with top margin
  statMutedCenter: "text-center p-3 bg-muted rounded-lg",  // Centered stat boxes
  
  // ─────────────────────────────────────────────────────────────────
  // CONTENT PATTERNS - For consistent card content layouts
  // ─────────────────────────────────────────────────────────────────
  contentNoPadding: "p-0 sm:p-0",
  contentCompact: "p-3 pt-0",
  contentDivider: "divide-y",
} as const;

export type CardToken = keyof typeof CARDS;
